import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  IconButton, TextField, InputAdornment, Grid, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Alert, Snackbar,
  Tabs, Tab, Switch, FormControlLabel, Divider, Tooltip
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Add as AddIcon, CloudUpload as UploadIcon, Download as DownloadIcon,
  Inventory as InventoryIcon, Build as MaintenanceIcon,
  AddCircleOutline as AddLogIcon
} from '@mui/icons-material';
import api, { inventoryService } from '../services/api';

// Department codes mapping
const DEPARTMENTS = [
  { code: 'AD', name: 'Office Administration' },
  { code: 'PM', name: 'Production Management' },
  { code: 'AC', name: 'Acting' },
  { code: 'ED', name: 'Editing' },
  { code: 'CN', name: 'Cinematography' },
  { code: 'DR', name: 'Direction' },
  { code: 'SW', name: 'Script Writing' },
  { code: 'OP', name: 'Office Managed Properties' }
];

// Categories mapping
const CATEGORIES = [
  { code: 'EQ', name: 'Equipment' },
  { code: 'ST', name: 'Stationery' },
  { code: 'FN', name: 'Furniture' },
  { code: 'EL', name: 'Electronics' },
  { code: 'SW', name: 'Software' },
  { code: 'CP', name: 'Costume/Props' },
  { code: 'RA', name: 'Rental Asset' },
  { code: 'MS', name: 'Miscellaneous' }
];

// Status options
const STATUSES = ['USAGE', 'NOT IN USE', 'REPAIR', 'STOCK', 'DAMAGED'];

// Location options
const LOCATIONS = ['Karur', 'CBE'];

// Categories that support maintenance tracking
const MAINTENANCE_CATEGORIES = ['Electronics', 'Equipment'];

const SERVICE_TYPES = ['Routine', 'Repair', 'Inspection', 'Replacement', 'AMC'];

function InventoryManagement() {
  const { user } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [maintenanceFilter, setMaintenanceFilter] = useState('');
  
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [uploadDialog, setUploadDialog] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  
  const [selectedItems, setSelectedItems] = useState([]);
  
  const [formData, setFormData] = useState({
    particulars: '', department: '', category: '',
    status: 'USAGE', location: 'Karur', comments: '',
    // Purchase & identity
    purchaseDate: '', purchasePrice: '', vendorName: '', brandModel: '', deviceSerialNo: '',
    // Warranty
    warrantyYears: '', warrantyExpiryDate: '', warrantyNote: '',
    // Maintenance
    requiresMaintenance: false, maintenanceFrequencyDays: '',
    lastServiceDate: '', nextServiceDue: '',
    maintenanceAlert: false, alertDaysBefore: 7
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Maintenance tab state
  const [editTab, setEditTab] = useState(0);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [serviceLogsLoading, setServiceLogsLoading] = useState(false);
  const [addLogDialog, setAddLogDialog] = useState(false);
  const [editLogTarget, setEditLogTarget] = useState(null); // null = add mode, log obj = edit mode
  const [logForm, setLogForm] = useState({
    serviceDate: new Date().toISOString().split('T')[0],
    serviceType: 'Routine', serviceProvider: '',
    serviceCost: '', serviceNotes: '', nextServiceDate: ''
  });

  useEffect(() => {
    fetchInventory();
  }, [search, departmentFilter, statusFilter, categoryFilter, locationFilter]);

  // Client-side maintenance filter applied on top of server-side results
  const filteredInventory = maintenanceFilter ? inventory.filter(item => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const warrantyExpiry = item.warrantyExpiryDate ? new Date(item.warrantyExpiryDate) : null;
    const nextDue = item.nextServiceDue ? new Date(item.nextServiceDue) : null;
    switch (maintenanceFilter) {
      case 'overdue':
        return item.requiresMaintenance && nextDue && nextDue < today;
      case 'due_soon':
        return item.requiresMaintenance && nextDue && nextDue >= today && (nextDue - today) / 86400000 <= 30;
      case 'up_to_date':
        return item.requiresMaintenance && nextDue && nextDue >= today && (nextDue - today) / 86400000 > 30;
      case 'no_schedule':
        return !item.requiresMaintenance;
      case 'warranty_expired':
        return warrantyExpiry && warrantyExpiry < today;
      case 'warranty_expiring':
        return warrantyExpiry && warrantyExpiry >= today && (warrantyExpiry - today) / 86400000 <= 60;
      case 'warranty_active':
        return warrantyExpiry && warrantyExpiry >= today && (warrantyExpiry - today) / 86400000 > 60;
      default: return true;
    }
  }) : inventory;

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = {
        search,
        department: departmentFilter || 'all',
        status: statusFilter || 'all',
        category: categoryFilter || 'all',
        location: locationFilter || 'all',
        limit: 1000
      };
      
      const response = await inventoryService.getAll(params);
      setInventory(response.data.data.inventory || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      showSnackbar('Error fetching inventory', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      // Extract department code from the selected department name
      const deptCode = DEPARTMENTS.find(d => d.name === formData.department)?.code || formData.department;
      
      const dataToSend = {
        ...formData,
        department: deptCode  // Send code instead of full name
      };
      
      await inventoryService.create(dataToSend);
      setAddDialog(false);
      resetForm();
      fetchInventory();
      showSnackbar('Inventory item added successfully', 'success');
    } catch (error) {
      console.error('Error adding item:', error);
      showSnackbar(error.response?.data?.message || 'Error adding item', 'error');
    }
  };

  const handleEdit = async () => {
    try {
      await inventoryService.update(editDialog.item.id, {
        particulars: formData.particulars,
        status: formData.status,
        comments: formData.comments,
        purchaseDate: formData.purchaseDate || null,
        purchasePrice: formData.purchasePrice || null,
        vendorName: formData.vendorName || null,
        brandModel: formData.brandModel || null,
        deviceSerialNo: formData.deviceSerialNo || null,
        warrantyYears: formData.warrantyYears || null,
        warrantyExpiryDate: formData.warrantyExpiryDate || null,
        warrantyNote: formData.warrantyNote || null,
        requiresMaintenance: formData.requiresMaintenance,
        maintenanceFrequencyDays: formData.maintenanceFrequencyDays || null,
        lastServiceDate: formData.lastServiceDate || null,
        nextServiceDue: formData.nextServiceDue || null,
        maintenanceAlert: formData.maintenanceAlert,
        alertDaysBefore: formData.alertDaysBefore || 7
      });
      setEditDialog({ open: false, item: null });
      resetForm();
      fetchInventory();
      showSnackbar('Inventory item updated successfully', 'success');
    } catch (error) {
      console.error('Error updating item:', error);
      showSnackbar('Error updating item', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await inventoryService.delete(deleteDialog.id);
      setDeleteDialog({ open: false, id: null });
      fetchInventory();
      showSnackbar('Inventory item deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting item:', error);
      showSnackbar('Error deleting item', 'error');
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Delete all selected items
      await Promise.all(selectedItems.map(id => inventoryService.delete(id)));
      setBulkDeleteDialog(false);
      setSelectedItems([]);
      fetchInventory();
      showSnackbar(`${selectedItems.length} items deleted successfully`, 'success');
    } catch (error) {
      console.error('Error bulk deleting:', error);
      showSnackbar('Error deleting items', 'error');
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedItems(inventory.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  const handleExport = async () => {
    try {
      const params = {
        department: departmentFilter || 'all',
        status: statusFilter || 'all',
        category: categoryFilter || 'all',
        location: locationFilter || 'all'
      };
      
      // Use the API service which includes authentication headers
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1'}/inventory/export?${new URLSearchParams(params)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'x-user-role': JSON.parse(localStorage.getItem('user') || '{}').role || '',
            'x-user-id': JSON.parse(localStorage.getItem('user') || '{}').id || '',
            'x-user-name': JSON.parse(localStorage.getItem('user') || '{}').username || ''
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob and download it
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showSnackbar('Inventory exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting:', error);
      showSnackbar('Error exporting to Excel', 'error');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      showSnackbar('Please select a file', 'warning');
      return;
    }
    
    try {
      const response = await inventoryService.importFromExcel(selectedFile);
      setUploadDialog(false);
      setSelectedFile(null);
      fetchInventory();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      console.error('Error importing:', error);
      showSnackbar(error.response?.data?.message || 'Error importing from Excel', 'error');
    }
  };

  const openEditDialog = (item) => {
    setFormData({
      particulars: item.particulars, department: item.department,
      category: item.category, status: item.status,
      location: item.location, comments: item.comments || '',
      purchaseDate: item.purchaseDate || '', purchasePrice: item.purchasePrice || '',
      vendorName: item.vendorName || '', brandModel: item.brandModel || '',
      deviceSerialNo: item.deviceSerialNo || '',
      warrantyYears: item.warrantyYears || '', warrantyExpiryDate: item.warrantyExpiryDate || '',
      warrantyNote: item.warrantyNote || '',
      requiresMaintenance: !!item.requiresMaintenance,
      maintenanceFrequencyDays: item.maintenanceFrequencyDays || '',
      lastServiceDate: item.lastServiceDate || '', nextServiceDue: item.nextServiceDue || '',
      maintenanceAlert: !!item.maintenanceAlert,
      alertDaysBefore: item.alertDaysBefore ?? 7
    });
    setEditTab(0);
    setEditDialog({ open: true, item });
    // Fetch service logs if maintenance category
    if (MAINTENANCE_CATEGORIES.includes(item.category)) {
      setServiceLogsLoading(true);
      api.get(`/inventory/${item.id}/service-logs`)
        .then(r => setServiceLogs(r.data.data || []))
        .catch(() => setServiceLogs([]))
        .finally(() => setServiceLogsLoading(false));
    }
  };

  const resetForm = () => {
    setFormData({
      particulars: '', department: '', category: '',
      status: 'USAGE', location: 'Karur', comments: '',
      purchaseDate: '', purchasePrice: '', vendorName: '', brandModel: '', deviceSerialNo: '',
      warrantyYears: '', warrantyExpiryDate: '', warrantyNote: '',
      requiresMaintenance: false, maintenanceFrequencyDays: '',
      lastServiceDate: '', nextServiceDue: '',
      maintenanceAlert: false, alertDaysBefore: 7
    });
    setServiceLogs([]);
  };

  const showSnackbar = (message, severity = 'success') => {    setSnackbar({ open: true, message, severity });
  };

  const handleAddServiceLog = async () => {
    // Validate dates
    const isValidDate = (d) => d && /^\d{4}-\d{2}-\d{2}$/.test(d) && !isNaN(new Date(d));
    if (!isValidDate(logForm.serviceDate)) { showSnackbar('Invalid service date', 'error'); return; }
    if (logForm.nextServiceDate && !isValidDate(logForm.nextServiceDate)) { showSnackbar('Invalid next service date — check the year', 'error'); return; }

    try {
      const payload = {
        serviceDate: logForm.serviceDate,
        serviceType: logForm.serviceType,
        serviceProvider: logForm.serviceProvider || null,
        serviceCost: logForm.serviceCost || null,
        serviceNotes: logForm.serviceNotes || null,
        nextServiceDate: logForm.nextServiceDate || null
      };

      if (editLogTarget) {
        await api.put(`/inventory/service-logs/${editLogTarget.id}`, payload);
      } else {
        await api.post(`/inventory/${editDialog.item.id}/service-logs`, payload);
      }

      const r = await api.get(`/inventory/${editDialog.item.id}/service-logs`);
      setServiceLogs(r.data.data || []);
      if (!editLogTarget) {
        setFormData(p => ({ ...p, lastServiceDate: logForm.serviceDate, nextServiceDue: logForm.nextServiceDate || p.nextServiceDue }));
      }
      setAddLogDialog(false);
      setEditLogTarget(null);
      setLogForm({ serviceDate: new Date().toISOString().split('T')[0], serviceType: 'Routine', serviceProvider: '', serviceCost: '', serviceNotes: '', nextServiceDate: '' });
      showSnackbar(editLogTarget ? 'Service log updated' : 'Service log added', 'success');
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Failed to save service log', 'error');
    }
  };

  const openEditLog = (log) => {
    setEditLogTarget(log);
    setLogForm({
      serviceDate:     log.serviceDate     || '',
      serviceType:     log.serviceType     || 'Routine',
      serviceProvider: log.serviceProvider || '',
      serviceCost:     log.serviceCost     || '',
      serviceNotes:    log.serviceNotes    || '',
      nextServiceDate: log.nextServiceDate || ''
    });
    setAddLogDialog(true);
  };

  const handleDeleteServiceLog = async (logId) => {
    if (!window.confirm('Delete this service log?')) return;
    try {
      await api.delete(`/inventory/service-logs/${logId}`);
      setServiceLogs(prev => prev.filter(l => l.id !== logId));
      showSnackbar('Service log deleted', 'success');
    } catch (e) {
      showSnackbar('Failed to delete log', 'error');
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const maintenanceStatus = (item) => {
    if (!item.requiresMaintenance || !item.nextServiceDue) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(item.nextServiceDue);
    const daysLeft = Math.round((due - today) / 86400000);
    if (daysLeft < 0) return { label: 'Overdue', color: 'error' };
    if (daysLeft <= 30) return { label: `Due in ${daysLeft}d`, color: 'warning' };
    return { label: 'Up to Date', color: 'success' };
  };

  const getStatusColor = (status) => {
    const colors = {
      'USAGE': 'success',
      'NOT IN USE': 'default',
      'REPAIR': 'warning',
      'STOCK': 'info',
      'DAMAGED': 'error'
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <InventoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Inventory Management
            </Typography>
            {!loading && (
              <Typography variant="body2" color="text.secondary">
                Total: {filteredInventory.length} item{filteredInventory.length !== 1 ? 's' : ''}{maintenanceFilter ? ` (filtered from ${inventory.length})` : ''}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {selectedItems.length > 0 && user?.role === 'owner' && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteDialog(true)}
              size="small"
            >
              Delete ({selectedItems.length})
            </Button>
          )}
          {user?.role === 'owner' && (
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialog(true)}
              size="small"
            >
              Import Excel
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            size="small"
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialog(true)}
            size="small"
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search particulars or category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2.25}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {DEPARTMENTS.map(dept => (
                    <MenuItem key={dept.code} value={dept.code}>{dept.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.25}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUSES.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.25}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {CATEGORIES.map(cat => (
                    <MenuItem key={cat.code} value={cat.name}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.25}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  label="Location"
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {LOCATIONS.map(loc => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2.25}>
              <FormControl fullWidth>
                <InputLabel>Maintenance / Warranty</InputLabel>
                <Select
                  value={maintenanceFilter}
                  label="Maintenance / Warranty"
                  onChange={(e) => setMaintenanceFilter(e.target.value)}
                >
                  <MenuItem value="">All Items</MenuItem>
                  <MenuItem value="overdue">🔴 Service Overdue</MenuItem>
                  <MenuItem value="due_soon">🟠 Due Within 30 Days</MenuItem>
                  <MenuItem value="up_to_date">🟢 Service Up to Date</MenuItem>
                  <MenuItem value="no_schedule">⚪ No Maintenance Schedule</MenuItem>
                  <MenuItem value="warranty_expired">⛔ Warranty Expired</MenuItem>
                  <MenuItem value="warranty_expiring">⚠️ Warranty Expiring (60d)</MenuItem>
                  <MenuItem value="warranty_active">✅ Warranty Active</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table - Desktop */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {user?.role === 'owner' && (
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={filteredInventory.length > 0 && selectedItems.length === filteredInventory.length}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                )}
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Particulars</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Comments</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user?.role === 'owner' ? 8 : 7} align="center">
                    <Typography color="text.secondary" py={3}>
                      {maintenanceFilter ? 'No items match this maintenance filter.' : 'No inventory items found. Click "Add Item" to create your first entry.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInventory.map((item) => (
                  <TableRow 
                    key={item.id} 
                    hover 
                    selected={selectedItems.includes(item.id)}
                    sx={{ backgroundColor: selectedItems.includes(item.id) ? 'action.selected' : 'inherit' }}
                  >
                    {user?.role === 'owner' && (
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {item.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.particulars}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.departmentName || DEPARTMENTS.find(d => d.code === item.department)?.name || item.department}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Chip label={item.status} color={getStatusColor(item.status)} size="small" />
                        {maintenanceStatus(item) && (
                          <Chip label={maintenanceStatus(item).label} color={maintenanceStatus(item).color} size="small" variant="outlined" icon={<MaintenanceIcon sx={{ fontSize: 12 }} />} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {item.comments || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, id: item.id })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Card View - Mobile */}
      {!loading && (
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {inventory.length === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary" align="center" py={3}>
                  No inventory items found. Click "Add Item" to create your first entry.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={2}>
              {filteredInventory.map((item) => (
                <Card key={item.id} sx={{ position: 'relative' }}>
                  <CardContent>
                    {user?.role === 'owner' && (
                      <Box position="absolute" top={8} right={8}>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          style={{ cursor: 'pointer', width: 20, height: 20 }}
                        />
                      </Box>
                    )}
                    
                    <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                      {item.code}
                    </Typography>
                    
                    <Typography variant="body1" fontWeight={600} gutterBottom>
                      {item.particulars}
                    </Typography>
                    
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Department:</Typography>
                        <Typography variant="body2">
                          {item.departmentName || DEPARTMENTS.find(d => d.code === item.department)?.name || item.department}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Category:</Typography>
                        <Typography variant="body2">{item.category}</Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Status:</Typography>
                        <Box mt={0.5}>
                          <Chip
                            label={item.status}
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Location:</Typography>
                        <Typography variant="body2">{item.location}</Typography>
                      </Grid>
                      
                      {item.comments && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Comments:</Typography>
                          <Typography variant="body2">{item.comments}</Typography>
                        </Grid>
                      )}
                    </Grid>
                    
                    <Box display="flex" gap={1} mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEditDialog(item)}
                        fullWidth
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteDialog({ open: true, id: item.id })}
                        fullWidth
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialog} onClose={() => { setAddDialog(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Inventory Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info">
              Code will be auto-generated: JE-{formData.location === 'Karur' ? 'KR' : 'CBE'}-
              {formData.department ? formData.department.substring(0, 2).toUpperCase() : 'XX'}-
              {CATEGORIES.find(c => c.name === formData.category)?.code || 'YY'}-####
            </Alert>
            
            <TextField
              fullWidth
              label="Particulars (Item Name)"
              value={formData.particulars}
              onChange={(e) => setFormData({ ...formData, particulars: e.target.value })}
              required
            />
            
            <FormControl fullWidth required>
              <InputLabel>Department</InputLabel>
              <Select
                value={formData.department}
                label="Department"
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              >
                {DEPARTMENTS.map(dept => (
                  <MenuItem key={dept.code} value={dept.name}>{dept.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {CATEGORIES.map(cat => (
                  <MenuItem key={cat.code} value={cat.name}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {STATUSES.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth required>
              <InputLabel>Location</InputLabel>
              <Select
                value={formData.location}
                label="Location"
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              >
                {LOCATIONS.map(loc => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Comments (Optional)"
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialog(false); resetForm(); }}>Cancel</Button>
          <Button 
            onClick={handleAdd} 
            variant="contained"
            disabled={!formData.particulars || !formData.department || !formData.category || !formData.location}
          >
            Add Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => { setEditDialog({ open: false, item: null }); resetForm(); }} maxWidth="md" fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle sx={{ pb: 0 }}>
          Edit — {editDialog.item?.code}
        </DialogTitle>

        {/* Tabs — Maintenance tab only for Electronics/Equipment */}
        <Tabs value={editTab} onChange={(_, v) => setEditTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Details" />
          {MAINTENANCE_CATEGORIES.includes(editDialog.item?.category) && (
            <Tab label={<Box display="flex" alignItems="center" gap={0.5}><MaintenanceIcon fontSize="small" />Maintenance</Box>} />
          )}
        </Tabs>

        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          {/* ── Tab 0: Details ── */}
          {editTab === 0 && (
            <Stack spacing={2} mt={1}>
              <Alert severity="warning">Code, Department, Category, and Location cannot be changed</Alert>
              <TextField fullWidth label="Code" value={editDialog.item?.code || ''} disabled InputProps={{ readOnly: true }} />
              <TextField fullWidth label="Particulars (Item Name)" value={formData.particulars}
                onChange={(e) => setFormData({ ...formData, particulars: e.target.value })} required />
              <TextField fullWidth label="Department"
                value={editDialog.item?.departmentName || DEPARTMENTS.find(d => d.code === editDialog.item?.department)?.name || editDialog.item?.department || ''}
                disabled InputProps={{ readOnly: true }} />
              <TextField fullWidth label="Category" value={editDialog.item?.category || ''} disabled InputProps={{ readOnly: true }} />
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select value={formData.status} label="Status" onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth label="Location" value={editDialog.item?.location || ''} disabled InputProps={{ readOnly: true }} />
              <TextField fullWidth label="Comments" value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })} multiline rows={3} />
            </Stack>
          )}

          {/* ── Tab 1: Maintenance ── */}
          {editTab === 1 && (
            <Box mt={1}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>PURCHASE & IDENTITY</Typography>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Purchase Date" type="date" InputLabelProps={{ shrink: true }} value={formData.purchaseDate} onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Purchase Price (₹)" type="number" value={formData.purchasePrice} onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Vendor / Supplier" value={formData.vendorName} onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Brand / Model" value={formData.brandModel} onChange={(e) => setFormData({ ...formData, brandModel: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Device Serial No." value={formData.deviceSerialNo} onChange={(e) => setFormData({ ...formData, deviceSerialNo: e.target.value })} /></Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>WARRANTY</Typography>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Warranty (Years)" type="number" value={formData.warrantyYears} onChange={(e) => setFormData({ ...formData, warrantyYears: e.target.value })} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Warranty Expiry" type="date" InputLabelProps={{ shrink: true }} value={formData.warrantyExpiryDate} onChange={(e) => setFormData({ ...formData, warrantyExpiryDate: e.target.value })} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Warranty Note / AMC" value={formData.warrantyNote} onChange={(e) => setFormData({ ...formData, warrantyNote: e.target.value })} /></Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>MAINTENANCE SCHEDULE</Typography>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel control={<Switch checked={!!formData.requiresMaintenance} onChange={(e) => setFormData({ ...formData, requiresMaintenance: e.target.checked })} />} label="Requires Maintenance" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel control={<Switch checked={!!formData.maintenanceAlert} onChange={(e) => setFormData({ ...formData, maintenanceAlert: e.target.checked })} />} label="Alert When Due" />
                </Grid>
                {formData.requiresMaintenance && (
                  <>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Frequency (Days)" type="number" value={formData.maintenanceFrequencyDays} onChange={(e) => setFormData({ ...formData, maintenanceFrequencyDays: e.target.value })} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Last Service Date" type="date" InputLabelProps={{ shrink: true }} value={formData.lastServiceDate} onChange={(e) => setFormData({ ...formData, lastServiceDate: e.target.value })} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Next Service Due" type="date" InputLabelProps={{ shrink: true }} value={formData.nextServiceDue} onChange={(e) => setFormData({ ...formData, nextServiceDue: e.target.value })} /></Grid>
                    {formData.maintenanceAlert && (
                      <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Alert Days Before" type="number" value={formData.alertDaysBefore} onChange={(e) => setFormData({ ...formData, alertDaysBefore: e.target.value })} /></Grid>
                    )}
                  </>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">SERVICE HISTORY</Typography>
                <Button size="small" startIcon={<AddLogIcon />} onClick={() => { setEditLogTarget(null); setLogForm({ serviceDate: new Date().toISOString().split('T')[0], serviceType: 'Routine', serviceProvider: '', serviceCost: '', serviceNotes: '', nextServiceDate: '' }); setAddLogDialog(true); }}>Add Service Log</Button>
              </Box>
              {serviceLogsLoading ? (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
              ) : serviceLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No service history yet</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Provider</strong></TableCell>
                      <TableCell><strong>Cost</strong></TableCell>
                      <TableCell><strong>Next Due</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceLogs.map(log => (
                      <TableRow key={log.id} hover>
                        <TableCell>{fmt(log.serviceDate)}</TableCell>
                        <TableCell><Chip label={log.serviceType} size="small" /></TableCell>
                        <TableCell>{log.serviceProvider || '—'}</TableCell>
                        <TableCell>{log.serviceCost ? `₹${parseFloat(log.serviceCost).toLocaleString('en-IN')}` : '—'}</TableCell>
                        <TableCell>{fmt(log.nextServiceDate)}</TableCell>
                        <TableCell sx={{ maxWidth: 150 }}><Typography variant="caption" noWrap>{log.serviceNotes || '—'}</Typography></TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit log">
                            <IconButton size="small" color="primary" onClick={() => openEditLog(log)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete log">
                            <IconButton size="small" color="error" onClick={() => handleDeleteServiceLog(log.id)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialog({ open: false, item: null }); resetForm(); }}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained">Update Item</Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Service Log Dialog */}
      <Dialog open={addLogDialog} onClose={() => { setAddLogDialog(false); setEditLogTarget(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editLogTarget ? 'Edit Service Log' : 'Add Service Log'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Service Date *" type="date" InputLabelProps={{ shrink: true }} value={logForm.serviceDate} onChange={(e) => setLogForm({ ...logForm, serviceDate: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Service Type *</InputLabel>
                <Select value={logForm.serviceType} label="Service Type *" onChange={(e) => setLogForm({ ...logForm, serviceType: e.target.value })}>
                  {SERVICE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Service Provider" value={logForm.serviceProvider} onChange={(e) => setLogForm({ ...logForm, serviceProvider: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Cost (₹)" type="number" value={logForm.serviceCost} onChange={(e) => setLogForm({ ...logForm, serviceCost: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Next Service Date" type="date" InputLabelProps={{ shrink: true }} value={logForm.nextServiceDate} onChange={(e) => setLogForm({ ...logForm, nextServiceDate: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" multiline rows={3} value={logForm.serviceNotes} onChange={(e) => setLogForm({ ...logForm, serviceNotes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddLogDialog(false); setEditLogTarget(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddServiceLog} disabled={!logForm.serviceDate || !logForm.serviceType}>
            {editLogTarget ? 'Update Log' : 'Save Log'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this inventory item? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)}>
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete {selectedItems.length} selected items? This action cannot be undone.
          </Alert>
          <Typography variant="body2">
            Selected items: {selectedItems.length}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained">
            Delete {selectedItems.length} Items
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => { setUploadDialog(false); setSelectedFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Import from Excel</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info">
              Upload an Excel file with columns: CODE, PARTICULARS, DEPARTMENT, STATUS, THINGS (Category)
            </Alert>
            
            <Button
              variant="outlined"
              component="label"
              fullWidth
            >
              {selectedFile ? selectedFile.name : 'Choose Excel File'}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
            </Button>
            
            {selectedFile && (
              <Typography variant="body2" color="success.main">
                ✓ File selected: {selectedFile.name}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialog(false); setSelectedFile(null); }}>Cancel</Button>
          <Button 
            onClick={handleImport} 
            variant="contained"
            disabled={!selectedFile}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InventoryManagement;
